/**
 * Simple Firebase-backed login UI for the admin portal.
 *
 * @remarks
 * This component intentionally uses the lightweight firebase wrapper (`src/utils/firebase.ts` → `src/lib/firebase.ts`)
 * to avoid circular dependencies during auth bootstrap.
 *
 * Logging note: keep logs low-noise and avoid PII. Prefer `warn`/`error` for durable signal.
 */

import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Container,
    TextField,
    Typography,
} from "@mui/material";
import {
    GoogleAuthProvider,
    OAuthProvider,
    signInWithCustomToken,
    signInWithEmailAndPassword,
    signInWithPopup,
    type User,
} from "firebase/auth";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import FrontendLoggerFactory from "utils/logging/FrontendLoggerFactory";

import { getErrorMessage as getPublicErrorMessage } from "../../utils/errorHandling";
import { auth } from "../../utils/firebase";

const logger = FrontendLoggerFactory.getLogger().withComponent("SimpleLogin");
const AUTH_STATE_PROPAGATION_DELAY_MS = 500;

const waitForAuthStatePropagation = async (): Promise<void> =>
    new Promise(resolve => {
        setTimeout(resolve, AUTH_STATE_PROPAGATION_DELAY_MS);
    });

const sanitizeReturnUrl = (value: string | null): string | null => {
    if (!value) {
        return null;
    }

    // Security: only allow relative paths to avoid open redirects.
    if (!value.startsWith("/") || value.startsWith("//")) {
        return null;
    }

    return value;
};

interface FinalizeLoginArgs {
    user: User;
    successOperation: string;
    successMessage: string;
    missingAdminOperation: string;
    missingAdminMessage: string;
    destination: string;
}

interface PopupLoginArgs {
    provider: GoogleAuthProvider | OAuthProvider;
    startOperation: string;
    successOperation: string;
    successMessage: string;
    missingAdminOperation: string;
    missingAdminMessage: string;
    failureOperation: string;
    failureMessage: string;
    failureFallbackMessage: string;
    destination: string;
}

const SimpleLogin: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from || "/";

    const tokenLoginHandledRef = useRef(false);

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const finalizeLogin = async ({
        user,
        successOperation,
        successMessage,
        missingAdminOperation,
        missingAdminMessage,
        destination,
    }: FinalizeLoginArgs): Promise<void> => {
        const claims = await user.getIdTokenResult();
        const hasAdminClaim = !!claims.claims["admin"];

        if (hasAdminClaim) {
            logger.debug(successMessage, {
                operation: successOperation,
                userId: user.uid,
            });
        } else {
            logger.warn(missingAdminMessage, {
                operation: missingAdminOperation,
                userId: user.uid,
            });
        }

        await waitForAuthStatePropagation();
        navigate(destination);
    };

    const handlePopupLogin = async ({
        provider,
        startOperation,
        successOperation,
        successMessage,
        missingAdminOperation,
        missingAdminMessage,
        failureOperation,
        failureMessage,
        failureFallbackMessage,
        destination,
    }: PopupLoginArgs): Promise<void> => {
        setLoading(true);
        setError("");

        try {
            logger.debug(`Starting ${startOperation} login`, {
                operation: startOperation,
            });

            const result = await signInWithPopup(auth, provider);
            await finalizeLogin({
                user: result.user,
                successOperation,
                successMessage,
                missingAdminOperation,
                missingAdminMessage,
                destination,
            });
        } catch (loginError) {
            logger.error(
                failureMessage,
                loginError instanceof Error ? loginError : undefined,
                { operation: failureOperation },
            );
            setError(getPublicErrorMessage(loginError, failureFallbackMessage));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const token = params.get("token");

        if (!token || tokenLoginHandledRef.current) {
            return;
        }

        tokenLoginHandledRef.current = true;

        // Important: do not log raw tokens (they are credentials). Keep logs low-noise.
        logger.info("Detected token-based login attempt", {
            operation: "login_custom_token_detected",
        });

        const returnUrlParam =
            params.get("return_url") ?? params.get("returnUrl");
        const returnUrl = sanitizeReturnUrl(returnUrlParam);
        const redirectTarget = returnUrl ?? from;

        setLoading(true);
        setError("");

        (async () => {
            try {
                // Keep sessions deterministic: drop any existing Firebase session before signing in.
                await auth.signOut().catch(() => undefined);

                await signInWithCustomToken(auth, token);
                await waitForAuthStatePropagation();
                navigate(redirectTarget, { replace: true });
            } catch (loginError) {
                tokenLoginHandledRef.current = false;
                logger.error(
                    "Custom token login failed; falling back to manual login",
                    loginError instanceof Error ? loginError : undefined,
                    { operation: "login_custom_token_failed" },
                );
                setError(
                    "Failed to sign in with custom token. The token may be invalid or expired.",
                );
            } finally {
                setLoading(false);
            }
        })();
    }, [from, location.search, navigate]);

    const handleEmailLogin = async (event: React.FormEvent) => {
        event.preventDefault();
        setLoading(true);
        setError("");

        try {
            logger.debug("Starting email/password login", {
                operation: "login_email_start",
            });

            const result = await signInWithEmailAndPassword(auth, email, password);
            await finalizeLogin({
                user: result.user,
                successOperation: "login_email_success",
                successMessage: "Email/password admin login successful",
                missingAdminOperation: "login_email_missing_admin",
                missingAdminMessage:
                    "User signed in without admin claim; navigation may fail behind protected routes",
                destination: from,
            });
        } catch (loginError) {
            logger.error(
                "Email/password login failed",
                loginError instanceof Error ? loginError : undefined,
                { operation: "login_email_failed" },
            );
            setError(getPublicErrorMessage(loginError, "Login failed"));
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleLogin = async () => {
        await handlePopupLogin({
            provider: new GoogleAuthProvider(),
            startOperation: "login_google_start",
            successOperation: "login_google_success",
            successMessage: "Google admin login successful",
            missingAdminOperation: "login_google_missing_admin",
            missingAdminMessage:
                "User signed in without admin claim; navigation may fail behind protected routes",
            failureOperation: "login_google_failed",
            failureMessage: "Google login failed",
            failureFallbackMessage: "Google login failed",
            destination: from,
        });
    };

    const handleXeroLogin = async () => {
        const provider = new OAuthProvider("oidc.xero");
        provider.addScope("email");
        provider.addScope("profile");

        await handlePopupLogin({
            provider,
            startOperation: "login_xero_start",
            successOperation: "login_xero_success",
            successMessage: "Xero admin login successful",
            missingAdminOperation: "login_xero_missing_admin",
            missingAdminMessage:
                "User signed in without admin claim; navigation may fail behind protected routes",
            failureOperation: "login_xero_failed",
            failureMessage: "Xero login failed",
            failureFallbackMessage: "Xero login failed",
            destination: from,
        });
    };

    const handleQuickBooksLogin = async () => {
        // QuickBooks uses a Firebase OIDC provider configured as `oidc.intuit`.
        const provider = new OAuthProvider("oidc.intuit");

        // Intuit OIDC expects standard scopes.
        provider.addScope("openid");
        provider.addScope("email");
        provider.addScope("profile");

        await handlePopupLogin({
            provider,
            startOperation: "login_quickbooks_start",
            successOperation: "login_quickbooks_success",
            successMessage: "QuickBooks admin login successful",
            missingAdminOperation: "login_quickbooks_missing_admin",
            missingAdminMessage:
                "User signed in without admin claim; navigation may fail behind protected routes",
            failureOperation: "login_quickbooks_failed",
            failureMessage: "QuickBooks login failed",
            failureFallbackMessage: "QuickBooks login failed",
            destination: from,
        });
    };

    return (
        <Container maxWidth='sm'>
            <Box
                sx={{
                    marginTop: 8,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}>
                <Card sx={{ width: "100%", maxWidth: 400 }}>
                    <CardContent sx={{ p: 4 }}>
                        <Typography
                            component='h1'
                            variant='h4'
                            align='center'
                            gutterBottom>
                            Admin Login
                        </Typography>

                        {error ? (
                            <Alert severity='error' sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        ) : null}

                        <Box component='form' onSubmit={handleEmailLogin} sx={{ mt: 1 }}>
                            <TextField
                                margin='normal'
                                required
                                fullWidth
                                id='email'
                                label='Email Address'
                                name='email'
                                autoComplete='email'
                                autoFocus
                                value={email}
                                onChange={event => setEmail(event.target.value)}
                                disabled={loading}
                            />
                            <TextField
                                margin='normal'
                                required
                                fullWidth
                                name='password'
                                label='Password'
                                type='password'
                                id='password'
                                autoComplete='current-password'
                                value={password}
                                onChange={event => setPassword(event.target.value)}
                                disabled={loading}
                            />
                            <Button
                                type='submit'
                                fullWidth
                                variant='contained'
                                sx={{ mt: 3, mb: 2 }}
                                disabled={loading}
                                startIcon={
                                    loading ? <CircularProgress size={20} /> : null
                                }>
                                Sign In with Email
                            </Button>
                        </Box>

                        <Button
                            fullWidth
                            variant='outlined'
                            onClick={handleGoogleLogin}
                            disabled={loading}
                            sx={{ mb: 1 }}
                            startIcon={
                                loading ? <CircularProgress size={20} /> : null
                            }>
                            Sign In with Google
                        </Button>

                        <Button
                            fullWidth
                            variant='outlined'
                            onClick={handleXeroLogin}
                            disabled={loading}
                            sx={{ mb: 1 }}
                            startIcon={
                                loading ? <CircularProgress size={20} /> : null
                            }>
                            Sign In with Xero
                        </Button>

                        <Button
                            fullWidth
                            variant='outlined'
                            onClick={handleQuickBooksLogin}
                            disabled={loading}
                            startIcon={
                                loading ? <CircularProgress size={20} /> : null
                            }>
                            Sign In with QuickBooks
                        </Button>
                    </CardContent>
                </Card>
            </Box>
        </Container>
    );
};

export default SimpleLogin;
